import { useEffect, useMemo, useRef, useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

const headerCellSx = {
	fontWeight: 700,
	fontSize: "0.68rem",
	letterSpacing: 0.3,
	textTransform: "uppercase",
	whiteSpace: "nowrap",
	bgcolor: "#fafafa",
	borderBottom: "1px solid #e5e7eb",
	color: "#6b7280",
	py: 1.1,
	px: 1.5,
};

const bodyCellSx = {
	whiteSpace: "nowrap",
	py: 1.05,
	px: 1.5,
	fontSize: "0.8125rem",
	borderBottom: "1px solid #f1f5f9",
	color: "#111827",
};

function labelFor(key) {
	return String(key)
		.split("_")
		.filter(Boolean)
		.map((part) => {
			if (part.length <= 4 && part === part.toUpperCase()) return part;
			return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
		})
		.join(" ");
}

function isNumericValue(value) {
	if (typeof value === "number") return Number.isFinite(value);
	if (typeof value !== "string") return false;
	const text = value.trim();
	if (!text || text.length > 16) return false;
	return /^-?\d+(\.\d+)?$/.test(text);
}

function isMeasureColumn(key, sample) {
	if (!sample.length) return false;
	const identifier = /(code|barcode|bill|document|sku|invoice|order_?no|_no$)/i.test(key);
	if (identifier && !/(qty|quantity|value|amount|diff)/i.test(key)) return false;
	const allNumbers = sample.every((value) => typeof value === "number" && Number.isFinite(value));
	if (allNumbers) return true;
	return /(qty|quantity|value|amount|diff|price|count|total|amt|tax|mrp|soh)/i.test(key) && sample.every(isNumericValue);
}

function isSummaryRow(row, firstKey) {
	const value = String(row?.[firstKey] ?? "")
		.trim()
		.toUpperCase();
	return value === "TOTAL" || value === "ALL";
}

function formatCell(value, numeric) {
	if (value === null || value === undefined || value === "") return "—";
	if (numeric) {
		const amount = Number(value);
		if (!Number.isFinite(amount)) return String(value);
		return amount.toLocaleString("en-IN", { maximumFractionDigits: 2 });
	}
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
}

function compareValues(left, right, numeric) {
	if (numeric) return (Number(left) || 0) - (Number(right) || 0);
	return String(left ?? "").localeCompare(String(right ?? ""), undefined, { numeric: true, sensitivity: "base" });
}

function isSimpleStatus(status) {
	const upper = status.toUpperCase();
	return upper === "MATCHED" || upper === "NOT MATCHED" || upper.startsWith("NOT FOUND");
}

function statusTone(status) {
	const upper = status.toUpperCase();
	if (upper === "MATCHED") return { color: "#047857", bgcolor: "#ecfdf5" };
	if (upper.startsWith("NOT FOUND")) return { color: "#92400e", bgcolor: "#fffbeb" };
	return { color: "#b91c1c", bgcolor: "#fef2f2" };
}

function SummaryStat({ label, value, color, bgcolor }) {
	return (
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "baseline",
				gap: 0.75,
				px: 1,
				py: 0.45,
				borderRadius: 1,
				color,
				bgcolor,
			}}>
			<Typography component="span" sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase" }}>
				{label}
			</Typography>
			<Typography component="span" sx={{ fontSize: "0.8125rem", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
				{value}
			</Typography>
		</Box>
	);
}

const ResultTable = ({ rows = [], onViewChange }) => {
	const [query, setQuery] = useState("");
	const onViewChangeRef = useRef(onViewChange);
	onViewChangeRef.current = onViewChange;
	const [sort, setSort] = useState({ key: "", dir: "asc" });
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);

	const columns = useMemo(() => {
		const keys = Object.keys(rows[0] || {});
		return keys.map((key) => {
			const sample = rows
				.slice(0, 40)
				.map((row) => row[key])
				.filter((value) => value !== null && value !== undefined && value !== "");
			const numeric = isMeasureColumn(key, sample);
			return { key, label: labelFor(key), numeric, diff: /diff/i.test(key) && numeric };
		});
	}, [rows]);

	const firstKey = columns[0]?.key;

	useEffect(() => {
		setQuery("");
		setSort({ key: "", dir: "asc" });
		setPage(0);
	}, [rows]);

	const { summary, bodyRows } = useMemo(() => {
		if (!firstKey) return { summary: null, bodyRows: rows };
		const summaryRow = rows.find((row) => isSummaryRow(row, firstKey)) || null;
		return {
			summary: summaryRow,
			bodyRows: summaryRow ? rows.filter((row) => row !== summaryRow) : rows,
		};
	}, [rows, firstKey]);

	const filteredRows = useMemo(() => {
		const text = query.trim().toLowerCase();
		if (!text) return bodyRows;
		return bodyRows.filter((row) =>
			columns.some((column) => String(row[column.key] ?? "").toLowerCase().includes(text))
		);
	}, [bodyRows, columns, query]);

	const sortedRows = useMemo(() => {
		if (!sort.key) return filteredRows;
		const column = columns.find((item) => item.key === sort.key);
		const dir = sort.dir === "desc" ? -1 : 1;
		return filteredRows.slice().sort((left, right) => compareValues(left[sort.key], right[sort.key], column?.numeric) * dir);
	}, [filteredRows, columns, sort]);

	useEffect(() => {
		const searching = Boolean(query.trim());
		onViewChangeRef.current?.({
			rows: summary && !searching ? [summary, ...sortedRows] : sortedRows,
			query: query.trim(),
		});
	}, [sortedRows, summary, query]);

	const pagedRows = useMemo(() => {
		const start = page * rowsPerPage;
		return sortedRows.slice(start, start + rowsPerPage);
	}, [sortedRows, page, rowsPerPage]);

	const reconciliation = useMemo(() => {
		const statusKey = columns.find((column) => column.key.toLowerCase() === "status")?.key;
		const diffKey = columns.find((column) => column.key.toLowerCase() === "diff")?.key;
		if (!statusKey && !diffKey) return null;
		const counts = new Map();
		let matched = 0;
		let notMatched = 0;
		let notFound = 0;
		let diffSum = 0;
		sortedRows.forEach((row) => {
			const status = String(row[statusKey] ?? "").trim();
			const upper = status.toUpperCase();
			if (status) counts.set(status, (counts.get(status) || 0) + 1);
			if (upper === "MATCHED") matched += 1;
			else if (upper.startsWith("NOT FOUND")) notFound += 1;
			else if (status) notMatched += 1;
			const amount = Number(row[diffKey]);
			if (Number.isFinite(amount)) diffSum += amount;
		});
		const statuses = [...counts.entries()]
			.sort((left, right) => {
				const leftMatched = left[0].toUpperCase() === "MATCHED" ? 0 : 1;
				const rightMatched = right[0].toUpperCase() === "MATCHED" ? 0 : 1;
				if (leftMatched !== rightMatched) return leftMatched - rightMatched;
				return right[1] - left[1];
			})
			.map(([label, count]) => ({ label, count, ...statusTone(label) }));
		const detailed = statuses.some((item) => !isSimpleStatus(item.label));
		return { statusKey, diffKey, matched, notMatched, notFound, diffSum, statuses, detailed };
	}, [columns, sortedRows]);

	if (!rows.length || !columns.length) return null;

	const toggleSort = (key, numeric) => {
		setSort((current) =>
			current.key === key
				? { key, dir: current.dir === "asc" ? "desc" : "asc" }
				: { key, dir: numeric ? "desc" : "asc" }
		);
		setPage(0);
	};

	const renderCell = (row, column, emphasized) => {
		const value = row[column.key];
		const amount = Number(value);
		const mismatch = column.diff && Number.isFinite(amount) && amount !== 0;
		return (
			<TableCell
				key={column.key}
				align={column.numeric ? "right" : "left"}
				sx={{
					...bodyCellSx,
					fontWeight: emphasized || mismatch ? 700 : 500,
					fontVariantNumeric: column.numeric ? "tabular-nums" : undefined,
					color: mismatch ? "#b91c1c" : emphasized ? "#111827" : bodyCellSx.color,
					bgcolor: emphasized ? "#f8fafc" : "inherit",
					...(emphasized ? { position: "sticky", top: 40, zIndex: 2 } : {}),
				}}>
				{formatCell(value, column.numeric)}
			</TableCell>
		);
	};

	return (
		<Stack
			sx={{
				border: "1px solid #e6e8ec",
				borderRadius: 1.5,
				overflow: "hidden",
				bgcolor: "#fff",
			}}>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "stretch", sm: "center" }}
				justifyContent="space-between"
				spacing={1}
				sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid #eef0f3", bgcolor: "#fff" }}>
				<Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
					<Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mr: 0.5 }}>
						{sortedRows.length.toLocaleString("en-IN")} {sortedRows.length === 1 ? "row" : "rows"}
					</Typography>
					{reconciliation?.statusKey ? (
						reconciliation.detailed ? (
							reconciliation.statuses.map((item) => (
								<SummaryStat
									key={item.label}
									label={item.label}
									value={item.count.toLocaleString("en-IN")}
									color={item.color}
									bgcolor={item.bgcolor}
								/>
							))
						) : (
							<>
								<SummaryStat
									label="Matched"
									value={reconciliation.matched.toLocaleString("en-IN")}
									color="#047857"
									bgcolor="#ecfdf5"
								/>
								<SummaryStat
									label="Not matched"
									value={reconciliation.notMatched.toLocaleString("en-IN")}
									color="#b91c1c"
									bgcolor="#fef2f2"
								/>
								{reconciliation.notFound > 0 ? (
									<SummaryStat
										label="Not found"
										value={reconciliation.notFound.toLocaleString("en-IN")}
										color="#92400e"
										bgcolor="#fffbeb"
									/>
								) : null}
							</>
						)
					) : null}
					{reconciliation?.diffKey ? (
						<SummaryStat
							label="Diff sum"
							value={reconciliation.diffSum.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
							color={reconciliation.diffSum === 0 ? "#047857" : "#b91c1c"}
							bgcolor={reconciliation.diffSum === 0 ? "#ecfdf5" : "#fef2f2"}
						/>
					) : null}
				</Stack>
				<TextField
					size="small"
					value={query}
					placeholder="Search rows"
					onChange={(event) => {
						setQuery(event.target.value);
						setPage(0);
					}}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
							</InputAdornment>
						),
					}}
					sx={{
						width: { xs: "100%", sm: 220 },
						"& .MuiOutlinedInput-root": {
							borderRadius: 1.5,
							fontSize: "0.8125rem",
							bgcolor: "#fff",
							"& fieldset": { borderColor: "#e5e7eb" },
							"&:hover fieldset": { borderColor: "#d1d5db" },
							"&.Mui-focused fieldset": { borderColor: "#6b7280", borderWidth: 1 },
						},
					}}
				/>
			</Stack>

			<TableContainer sx={{ maxHeight: 520 }}>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell key={column.key} align={column.numeric ? "right" : "left"} sx={headerCellSx}>
									<TableSortLabel
										active={sort.key === column.key}
										direction={sort.key === column.key ? sort.dir : "asc"}
										onClick={() => toggleSort(column.key, column.numeric)}
										sx={column.numeric ? { flexDirection: "row-reverse" } : undefined}>
										{column.label}
									</TableSortLabel>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{summary ? (
							<TableRow>{columns.map((column) => renderCell(summary, column, true))}</TableRow>
						) : null}
						{pagedRows.length ? (
							pagedRows.map((row, index) => (
								<TableRow key={index} hover sx={{ "&:hover td": { bgcolor: "#f9fafb" } }}>
									{columns.map((column) => renderCell(row, column, false))}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={columns.length} align="center" sx={{ py: 6, borderBottom: 0, color: "#6b7280" }}>
									No rows match your search
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<TablePagination
				component="div"
				count={sortedRows.length}
				page={page}
				onPageChange={(_, nextPage) => setPage(nextPage)}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={(event) => {
					setRowsPerPage(parseInt(event.target.value, 10));
					setPage(0);
				}}
				rowsPerPageOptions={[25, 50, 100]}
				sx={{
					borderTop: "1px solid #eef0f3",
					"& .MuiTablePagination-toolbar": { minHeight: 44 },
					"& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
						fontSize: "0.75rem",
						color: "#6b7280",
					},
				}}
			/>
		</Stack>
	);
};

export default ResultTable;
